import last from 'lodash/last';

import {Section, SectionItem} from 'common/components/items-menu/types';
import {findSelectedItems} from 'common/components/items-menu/utils';
import {ChangeEvent} from 'common/components/select/types';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {ActionType, trackLearningAction} from 'common/util/server-tracking';
import {BookmarkDateRange, DateRangeType} from 'common/widgets/date-range-picker/models/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  DisplayContext,
  FilterOperator,
  FilterValue,
  ListQuantifier,
  NumberOperator,
  PropertyType,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {generateFilterTrackingProps, transformFilterToPropertyItem} from 'common/widgets/query-block/filter-menu/util';
import {
  Action,
  EntryPart,
  FiltersOperator as EntrySectionFilterOperator,
  QueryFilterEntry,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {getSimilarDateRange} from 'common/widgets/date-range-picker-new/util';
import {getCurrentMoment} from 'common/util/date';

import {BaseEntryRow} from '../base';
import {EntryRow, EntryRowProps} from '../base/types';
import {OperatorDescription, PropertyTypeOperatorMap} from './constants';
import {FilterEntryRowEl} from './template';

type SelectedOperator = [FilterOperator, OperatorDescription];

export class FilterEntryRow extends BaseEntryRow<QueryFilterEntry> implements EntryRow<QueryFilterEntry> {
  filter: PropertyFilter;
  template = FilterEntryRowEl;

  constructor(props: EntryRowProps<QueryFilterEntry>) {
    super(props);
    this.filter = this.createFilter(props.entry.event);
  }

  /**
   * Generates a new empty QueryFilterEntry
   */
  static getNewEntry(): QueryFilterEntry {
    return {
      type: SelectorTypeDefinitions.Filter,
      filtersOperator: null,
      filters: [],
      event: null,
    };
  }

  createFilter(attrs: Partial<PropertyFilter>) {
    return PropertyFilterFactory.createPropertyFilter(attrs, {
      displayContext: DisplayContext.Default,
    });
  }

  isPlaceholder() {
    return !this.filter.isValid();
  }

  handlePropertyItemSelected(
    ev: CustomEvent<{item: PropertyFilter; trackingProps: Record<string, any>; queryReason: QueryReason}>,
  ) {
    const {item, trackingProps, queryReason} = ev.detail;
    this.updateEntry(item, {trackingProps, queryReason});
  }

  setFilterAttribute(attribute: keyof PropertyFilter, value) {
    this.updateEntry(this.filter.setAttribute(attribute, value));
  }

  handleFilterValueChange(value: FilterValue) {
    this.setFilterAttribute(`filterValue`, value);
  }

  /**
   * Updates the selected filter operator and attemps to preserve
   * any pre-existing selected filter values
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  handleFilterOperatorChange(ev: ChangeEvent<SectionItem<SelectedOperator>>) {
    const [filterOperator] = ev.detail.item.value;
    const filter = this.filter.setAttribute(`filterOperator`, filterOperator);

    const {filterValue: currentFilterValue, propertyType} = this.filter;
    const {filterValue: defaultFilterValue, filterOperator: defaultFilterOperator} = filter;

    // when changing between filter operators, we want to transition to a valid filter state
    // in some cases, when there's not enough information to fully construct a new filter
    // for the selected operator, we omit the validation stage to allow for user input
    let performValueValidation = true;

    // attempts to preserve the currently selected date filter by applying the new selected
    // operator which maps to a DateRangeType
    if (propertyType === PropertyType.Datetime) {
      const oldDateRange = this.filter.filterValue as DateRange;
      const similarDate = getSimilarDateRange(
        oldDateRange,
        getCurrentMoment({localized: true}),
        filterOperator as DateRangeType,
      );
      const filterValue = similarDate ? this.filter.createDateRange(similarDate) : (defaultFilterValue as DateRange);
      if (filterValue.isValid() && filterValue.type === filterOperator) {
        filter.filterValue = filterValue;
        filter.filterOperator = filterValue.type;
      }
    } else if (currentFilterValue && (propertyType === PropertyType.Number || propertyType === PropertyType.String)) {
      // attempts to preserve the first selected filter value entry from an array filterValue
      const isCurrentValueArray = Array.isArray(currentFilterValue);
      const operatorRequiresArrayValue = [
        NumberOperator.Between,
        NumberOperator.NotBetween,
        StringOperator.Equals,
        StringOperator.DoesNotEqual,
      ].includes(filterOperator as NumberOperator);

      if (operatorRequiresArrayValue) {
        const isNumericFilter = propertyType === PropertyType.Number;
        performValueValidation = false;
        filter.filterValue = isCurrentValueArray
          ? // keeps selected array as new value
            currentFilterValue
          : // populates a numeric filter [value, value]
            ((isNumericFilter ? [currentFilterValue, null] : [currentFilterValue]) as FilterValue);
      } else {
        // grabs first element from selected value when possible
        filter.filterValue = isCurrentValueArray ? currentFilterValue[0] : currentFilterValue;
      }
    } else {
      filter.filterValue = currentFilterValue;
    }

    // when value preservation logic fails to end in a valid state, we revert
    // to the generated defaults by the property filter class
    if (performValueValidation && filter.isValid() === false) {
      filter.filterOperator = defaultFilterOperator;
      filter.filterValue = defaultFilterValue;
    }

    // focus on filter value when operator changes filter to an invalid state
    !filter.isValid() && this.section.setActiveEntry(this.index, EntryPart.FilterValue);

    this.updateEntry(filter);
  }

  /**
   * Returns SecionItems for all avaialble operators
   *
   * TODO(pablo): figure out date modeling for the new UI model
   */
  getFilterOperatorsSections(): Array<Section<SelectedOperator>> {
    const {propertyType, filterOperator, filterValue} = this.filter;
    const isDateRange = propertyType === PropertyType.Datetime;
    const operators = isDateRange
      ? this.filter.createDateRange().getDateRangeTypeOptions()
      : this.filter.getFilterOperators();
    const operatorMap = PropertyTypeOperatorMap.get(this.filter.propertyType);

    const items: Array<SectionItem<SelectedOperator>> = Object.entries(operatorMap)
      .filter(([operator]) => operators.includes(operator as FilterOperator))
      .map(([operator, description]) => {
        return {
          label: `${description.symbol}` + (description.label ? ` (${description.label})` : ``),
          isSelected:
            isDateRange && filterValue
              ? (filterValue as BookmarkDateRange).type === operator
              : filterOperator === operator,
          value: [operator as FilterOperator, description],
        };
      });

    return [{items}];
  }

  getListQuantifierSections() {
    const listQuantifier = this.filter.listQuantifier;
    const items = [
      {label: `Any`, value: ListQuantifier.Any, isSelected: listQuantifier === ListQuantifier.Any},
      {label: `All`, value: ListQuantifier.All, isSelected: listQuantifier === ListQuantifier.All},
    ];
    return [{items}];
  }

  getSelectedOperator() {
    return last(findSelectedItems<SelectedOperator>(this.getFilterOperatorsSections()))?.value;
  }

  getSelectedProperty() {
    return transformFilterToPropertyItem(this.filter);
  }

  getFilterOperatorTooltip() {
    const [, mapping] = this.getSelectedOperator();
    return mapping?.label && {text: mapping.label};
  }

  getListQuantifierTooltip(listQuantifier: ListQuantifier) {
    return {
      text: `match ${listQuantifier}`,
    };
  }

  getFilterOperatorSymbol() {
    const [, mapping] = this.getSelectedOperator();
    return mapping.symbol;
  }

  handleEntryFilterOperatorChange(filtersOperator: EntrySectionFilterOperator) {
    const updatedEntry = {
      ...this.entry,
      filtersOperator,
    };
    this.section.updateEntries(Action.Edit, {entry: updatedEntry, index: this.index});
  }

  updateEntry(
    filter: PropertyFilter,
    {trackingProps, queryReason}: Partial<{trackingProps: Obj; queryReason: QueryReason}> = {},
  ) {
    const updatedEntry = {
      ...this.entry,
      event: filter instanceof PropertyFilter ? filter.getAttributes() : filter,
    };

    // when changing a committed filter to an invalid filter, or when the filter property
    // is selected for the first time, set focus to the filter value selection
    const isStagingChanges = this.section.isStagingEntryChanges(this.entry, updatedEntry);
    const hasProperty = this.filter.hasSelectedProperty();
    if (isStagingChanges || !hasProperty) {
      this.section.setActiveEntry(this.index, EntryPart.FilterValue);
    }

    // The filter-menu will pass through queryReason in which case we should use that queryReason.
    queryReason = queryReason || QueryReason.QbFilterUpdate;
    const updateStatus = this.section.updateEntries(Action.Edit, {
      entry: updatedEntry,
      index: this.index,
      trackingProps: trackingProps ?? generateFilterTrackingProps(filter),
      queryReason,
    });

    if (queryReason !== QueryReason.QbAutoTriggered && updateStatus.dispatchedUpdate) {
      // We don't want to track learning action until the complete filter has been created
      trackLearningAction({
        projectId: this.section.attr(`project-id`),
        reportName: this.section.attr(`track-name`),
        actionType: ActionType.Filter,
        resourceType: filter.resourceType,
      });
    }
  }
}
